import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import UserContext from "../context/UserContext";
import { api } from "../utils/apiHelper";
import "../styles/DigiManager.css";

const DigiStoreManager = () => {
  const { authUser } = useContext(UserContext);
  const [digiStore, setDigiStore] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingProject, setUploadingProject] = useState(null);
  const [editingField, setEditingField] = useState(null); // { projectId, field }
  const [editValue, setEditValue] = useState("");

  // Fetch DigiStore projects
  const fetchDigiStore = useCallback(async () => {
    if (!authUser) return;
    setLoading(true);
    try {
      const response = await api(`/digi-get-products?userId=${authUser._id}`, "GET");
      if (response.error) throw new Error(response.error);
      setDigiStore(response.digiStore || []);
    } catch (err) {
      console.error("🚨 Error fetching DigiStore:", err);
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    fetchDigiStore();
  }, [fetchDigiStore]);

  const handleEditClick = (projectId, field, currentValue) => {
  setEditingField({ projectId, field });
  setEditValue(currentValue);
};

const handleInputChange = (e) => setEditValue(e.target.value);

const handleEditSubmit = async (projectId, field) => {
  if (field === "quantity" || field === "price") {
    if (editValue === "" || isNaN(editValue)) return;  // Number fields check
  } else {
    if (!editValue.trim()) return;  // String fields check
  }

  try {
    await api("/update-title-desc-price", "POST", {
      userId: authUser._id,
      projectId,
      [field]: editValue,
    });

    setDigiStore((prev) =>
      prev.map((proj) =>
        proj.projectId === projectId ? { ...proj, [field]: editValue } : proj
      )
    );
  } catch (error) {
    console.error("❌ Update failed:", error);
  }

  setEditingField(null);
};


const handleDeleteImage = async (projectId) => {
  if (!authUser || !projectId) return;

  const confirmDelete = window.confirm("Are you sure you want to delete this image?");
  if (!confirmDelete) return;

  try {
    const response = await api("/delete-product-image", "POST", {
      userId: authUser._id,
      projectId,
    });

    if (response.error) {
      console.error("❌ Failed to delete image:", response.error);
      return;
    }

    // ✅ Set image to an empty string to match backend structure
    setDigiStore((prev) =>
      prev.map((proj) =>
        proj.projectId === projectId ? { ...proj, image: "", imageUrl: null } : proj
      )
    );
  } catch (error) {
    console.error("❌ Error deleting image:", error);
  }
};

const handleImageDrop = async (acceptedFiles, projectId) => {
  if (!authUser || !projectId || acceptedFiles.length === 0) return;

  const imageFile = acceptedFiles[0];

  try {
    // Step 1: Request signed upload URL
    const response = await api("/replace-product-image", "POST", {
      userId: authUser._id,
      projectId,
      fileName: imageFile.name,
      fileType: imageFile.type,
    });

    if (!response || !response.signedUrl || !response.key) {
      throw new Error("Failed to get signed image URL");
    }

    // Step 2: Upload image to S3
    await fetch(response.signedUrl, {
      method: "PUT",
      body: imageFile,
      headers: { "Content-Type": imageFile.type },
    });

    // Step 3: Update UI
    setDigiStore((prev) =>
      prev.map((proj) =>
        proj.projectId === projectId
          ? { ...proj, image: response.key } // backend should serve signed URL on next fetch
          : proj
      )
    );
  } catch (error) {
    console.error("🚨 Error uploading image:", error);
  }
};



  // Handle file upload
const onDrop = async (acceptedFiles, projectId) => {
  if (!authUser || !projectId) {
    console.error("❌ Missing authentication or projectId.");
    return;
  }

  setUploadingProject(projectId);

  try {
    await Promise.all(
      acceptedFiles.map(async (file) => {
        if (!file || !file.name) {
          console.error("❌ Invalid file:", file);
          return;
        }

        const userId = authUser._id;
        const fileId = uuidv4();
        

        // Step 1: Generate Signed URL
        const response = await api("/generate-upload-url", "POST", {
          userId,
          projectId,
          fileName: file.name,
          fileType: file.type,
        });

        if (!response || !response.signedUrl || !response.key) {
         
          return;
        }

      

        // Step 2: Upload File to S3
        await fetch(response.signedUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });


        // Step 3: Save File Record in MongoDB
        const projectDetails = digiStore.find((p) => p.projectId === projectId);
        const fileRecord = {
          key: response.key,
          fileName: file.name,
          fileId,
          projectId,
          projectTitle: projectDetails?.projectTitle || "Unknown",
          fileOrigin: projectId,
          createdAt: new Date(),
          url: `https://${process.env.AWS_BUCKET_NAME}.s3.${process.env.AWS_REGION}.amazonaws.com/${response.key}`,
          metadata: {},
        };


        const uploadResponse = await api("/upload-digi-file", "POST", {
          userId,
          ...fileRecord,
        });

        if (uploadResponse.error) {
          throw new Error("Failed to save file record");
        }


        // Step 4: Extract Metadata + Save to DB juice
        // digiRoutes 558
        const metadataResponse = await api("/saveler-metadata", "POST", {
          userId,
          projectId,
          fileId,
          key: response.key,
        });

        if (metadataResponse.error || !metadataResponse.metadata) {
          console.warn("⚠️ Metadata extraction failed or returned empty.");
        } else {
          console.log(`🎵 Extracted Metadata:`, metadataResponse.metadata);
        }

        // Step 5: Update UI with metadata
        setDigiStore((prev) =>
          prev.map((proj) =>
            proj.projectId === projectId
              ? {
                  ...proj,
                  files: proj.files.map((f) =>
                    f.fileId === fileId
                      ? { ...f, metadata: metadataResponse.metadata || {} }
                      : f
                  ),
                }
              : proj
          )
        );
      })
    );
  } catch (error) {
    console.error("🚨 Error uploading files:", error);
  } finally {
    setUploadingProject(null);
     alert("🎉 File added successfully!");

  }
};

const CustomAudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    setProgress((audio.currentTime / audio.duration) * 100);
    setDuration(audio.duration);
  };

  return (
    <div className="custom-audio-player">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        preload="none"
      />
      <button onClick={togglePlay} className="play-button">
        {isPlaying ? "⏸" : "▶️"}
      </button>
      <div className="progress-container">
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>
      {/*<div className="duration"> {duration ? `${Math.floor(duration)}s` : "--"} </div> */}
    </div>
  );
};


  return (
    <div className="digi-store-container">
  <h2>My Digital Products</h2>

  {loading ? (
    <p>Loading products...</p>
  ) : digiStore.length > 0 ? (
    digiStore.map((project) => (
      <div key={project.projectId} className="manager-product-card">
        {/* Product Image */}
  {project.imageUrl ? (
  <div className="manager-product-image">
    <img
      src={project.imageUrl}
      alt={project.projectTitle}
      style={{ maxWidth: "100%", borderRadius: "15px" }}
    />
    <button
      onClick={() => handleDeleteImage(project.projectId)}
      style={{
        marginTop: "8px",
        backgroundColor: "#f44336",
        color: "#fff",
        border: "none",
        padding: "6px 10px",
        borderRadius: "4px",
        cursor: "pointer"
      }}
    >
      Remove Image
    </button>
  </div>
) : (
  <ImageDropzone onDrop={handleImageDrop} projectId={project.projectId} />
)}


        {/* Editable Title */}
        {editingField?.projectId === project.projectId && editingField?.field === "projectTitle" ? (
          <input
            type="text"
            value={editValue}
            onChange={handleInputChange}
            onBlur={() => handleEditSubmit(project.projectId, "projectTitle")}
            onKeyDown={(e) =>
              e.key === "Enter" && handleEditSubmit(project.projectId, "projectTitle")
            }
            autoFocus
          />
        ) : (
          <h3
            onClick={() =>
              handleEditClick(project.projectId, "projectTitle", project.projectTitle)
            }
          >
            {project.projectTitle}
          </h3>
        )}

        {/* Editable Description */}
        <p>
          <strong>Description:</strong>{" "}
          {editingField?.projectId === project.projectId && editingField?.field === "description" ? (
            <input
              type="text"
              value={editValue}
              onChange={handleInputChange}
              onBlur={() => handleEditSubmit(project.projectId, "description")}
              onKeyDown={(e) =>
                e.key === "Enter" && handleEditSubmit(project.projectId, "description")
              }
              autoFocus
            />
          ) : (
            <span
              onClick={() =>
                handleEditClick(project.projectId, "description", project.description)
              }
            >
              {project.description}
            </span>
          )}
        </p>

        {/* Editable Price */}
        <p>
          <strong>Price:</strong>{" "}
          {editingField?.projectId === project.projectId && editingField?.field === "price" ? (
            <input
              type="number"
              value={editValue}
              onChange={handleInputChange}
              onBlur={() => handleEditSubmit(project.projectId, "price")}
              onKeyDown={(e) =>
                e.key === "Enter" && handleEditSubmit(project.projectId, "price")
              }
              autoFocus
            />
          ) : (
            <span onClick={() => handleEditClick(project.projectId, "price", project.price)}>
              ${project.price}
            </span>
          )}
        </p>

       {/* Editable Quantity */}
        <p>
          <strong>Quantity:</strong>{" "}
          {editingField?.projectId === project.projectId && editingField?.field === "quantity" ? (
            <input
              type="number"
              value={editValue}
              onChange={handleInputChange}
              onBlur={() => handleEditSubmit(project.projectId, "quantity")}
              onKeyDown={(e) =>
                e.key === "Enter" && handleEditSubmit(project.projectId, "quantity")
              }
              autoFocus
            />
          ) : (
            <span onClick={() => handleEditClick(project.projectId, "quantity", project.quantity)}>
              {project.quantity}
            </span>
          )}
        </p>


        {/* Add this below Price */}
        <p>
          <strong>License Type:</strong>{" "}
          {project.licenseType ? project.licenseType.charAt(0).toUpperCase() + project.licenseType.slice(1).replace("-", " ") : "Not specified"}
        </p>

        {/* File List */}
        {project.files.length > 0 ? (
          <ul className="new-list">
            {project.files.map((file) => (
              <li key={file.fileId}>
                <div className="audio-row">
  {file.url && (file.fileName.toLowerCase().endsWith('.mp3') || file.fileName.toLowerCase().endsWith('.wav')) && (
    <CustomAudioPlayer src={file.url} />
  )}
  <p className="file-name">{file.fileName}</p>
</div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No files uploaded yet.</p>
        )}

        {/* File Upload Dropzone */}
        <DropzoneComponent projectId={project.projectId} onDrop={onDrop} />
        {uploadingProject === project.projectId && <p>Uploading...</p>}
      </div>
    ))
  ) : (
    <p>No products found.</p>
  )}
</div>
  );
};

const ImageDropzone = ({ onDrop, projectId }) => {
  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [] },
    multiple: false,
    onDrop: (files) => onDrop(files, projectId),
  });

  return (
    <div {...getRootProps()} className="product-image-placeholder clickable-dropzone">
      <input {...getInputProps()} />
      <p>Click to add image</p>
    </div>
  );
};

// Dropzone component for handling file uploads
const DropzoneComponent = ({ projectId, onDrop }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      onDrop(acceptedFiles, projectId);
    },
  });

  return (
    <div {...getRootProps()} className="manager-dropzone">
      <input {...getInputProps()} />
      <p>Drag & drop files here or click to upload</p>
    </div>
  );
};

export default DigiStoreManager;
